import React, { useState } from 'react';
import { useNotifications } from '../contexts/NotificationContext';

const MetaCostIncreaseQuickActionModal = ({ record, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    action_type: 'follow_up',
    description: '',
    new_status: record?.status || 'pendiente',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotifications();

  const actionTypes = [
    { value: 'follow_up', label: '📞 Seguimiento Realizado', icon: '📞' },
    { value: 'status_changed', label: '🔄 Cambio de Estado', icon: '🔄' },
    { value: 'comment_added', label: '💬 Comentario Agregado', icon: '💬' },
    { value: 'email_sent', label: '📧 Email Enviado', icon: '📧' },
    { value: 'meeting_scheduled', label: '📅 Reunión Programada', icon: '📅' },
    { value: 'issue_resolved', label: '✅ Problema Resuelto', icon: '✅' },
    { value: 'escalated', label: '⚠️ Escalado', icon: '⚠️' }
  ];

  const statusOptions = [
    { value: 'pendiente', label: '⏳ Pendiente', color: 'text-yellow-600' },
    { value: 'en_proceso', label: '🔄 En Proceso', color: 'text-blue-600' },
    { value: 'completado', label: '✅ Completado', color: 'text-green-600' },
    { value: 'cancelado', label: '❌ Cancelado', color: 'text-red-600' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.description.trim()) {
      showNotification('Por favor ingresa una descripción de la acción', 'error');
      return;
    }

    if (!record?.id) {
      showNotification('Error: ID del registro no encontrado', 'error');
      return;
    }

    setLoading(true);
    try {
      // Preparar los datos de la acción
      const actionData = {
        action_type: formData.action_type,
        description: formData.description,
        details: formData.notes,
        user_name: 'Usuario Actual', // En producción esto vendría del contexto de usuario
        new_status: formData.action_type === 'status_changed' ? formData.new_status : null
      };

      // Usar la función del componente padre
      await onSave(record.id, actionData);
      
      showNotification('Acción registrada exitosamente', 'success');
      onClose();
    } catch (error) {
      console.error('Error al registrar acción:', error);
      showNotification('Error al registrar la acción: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getActionIcon = (actionType) => {
    const action = actionTypes.find(a => a.value === actionType);
    return action ? action.icon : '📝';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header - Fixed */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-6 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold flex items-center">
                <span className="text-3xl mr-3">⚡</span>
                Acción Rápida
              </h2>
              <p className="text-green-100 mt-1">
                Registrar acción para: <strong>{record?.nombre_cliente}</strong>
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Tipo de Acción */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Tipo de Acción *
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {actionTypes.map(action => (
                    <label
                      key={action.value}
                      className={`relative flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${
                        formData.action_type === action.value
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-green-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="action_type"
                        value={action.value}
                        checked={formData.action_type === action.value}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <span className="text-2xl mr-3">{action.icon}</span>
                      <span className="text-sm font-medium text-gray-900">
                        {action.label.replace(/^[^\s]+ /, '')}
                      </span>
                      {formData.action_type === action.value && (
                        <div className="absolute top-2 right-2">
                          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        </div>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              {/* Cambio de Estado (solo si se selecciona status_changed) */}
              {formData.action_type === 'status_changed' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Nuevo Estado *
                  </label>
                  <select
                    name="new_status"
                    value={formData.new_status}
                    onChange={handleChange}
                    className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                    required
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Descripción */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Descripción de la Acción *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe brevemente la acción realizada..."
                  rows="3"
                  className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                  required
                />
              </div>

              {/* Notas Adicionales */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Notas Adicionales
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Información adicional, próximos pasos, observaciones..."
                  rows="3"
                  className="w-full p-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                />
              </div>

              {/* Información del Cliente */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Información del Registro</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Cliente:</span>
                    <span className="ml-2 font-medium">{record?.nombre_cliente}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Célula:</span>
                    <span className="ml-2 font-medium">{record?.celula}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Estado Actual:</span>
                    <span className="ml-2 font-medium">{record?.status}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Campaña:</span>
                    <span className="ml-2 font-medium">{record?.campana}</span>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-end space-x-3 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 font-medium transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Registrando...
              </>
            ) : (
              <>
                <span className="text-xl mr-2">{getActionIcon(formData.action_type)}</span>
                Registrar Acción
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MetaCostIncreaseQuickActionModal;
